<template>
  <div class="app-container">
    <!-- 主内容区域 -->
    <div class="content-area">
      <!-- 首页 -->
      <div class="home-page" v-if="activePage === 'home'">
        <!-- 积分模块 -->
        <div class="credit-module glass-effect">
          <div class="credit-info">
            <div class="credit-amount">
              <span>剩余积分：</span>
              <span class="credit-number">{{ credits }}</span>
            </div>
            <button class="recharge-btn" @click="showRechargeModal = true">充值</button>
          </div>
        </div>

        <!-- 动作预设选择模块 -->
        <div class="action-select-module glass-effect">
          <div class="action-select" @click="toggleActionDropdown">
            <div class="selected-action">{{ selectedAction.name }}</div>
            <div class="dropdown-icon" :class="{ 'active': showActionDropdown }">▼</div>
          </div>
          <div class="action-dropdown" v-if="showActionDropdown">
            <div 
              v-for="(action, index) in actions" 
              :key="index" 
              class="action-item"
              @click="selectAction(action)"
            >
              {{ action.name }} (需{{ action.requiredImages }}张图片, {{ action.creditCost }}积分)
            </div>
          </div>
        </div>

        <!-- 图像上传模块 -->
        <div class="upload-module glass-effect">
          <div class="upload-title">
            <span>请添加{{ selectedAction.requiredImages }}张图片链接</span>
          </div>
          <div class="image-input-container" v-for="(image, index) in uploadedImages" :key="index">
            <div class="image-input-wrapper">
              <input 
                type="text" 
                class="image-url-input"
                v-model="image.url"
                placeholder="请输入图片URL"
                @change="validateImage(index)"
              />
              <div class="preview-remove" @click="removeImage(index)">×</div>
            </div>
            <div v-if="image.loading" class="image-loading">验证中...</div>
            <div v-else-if="image.error" class="image-error">{{ image.error }}</div>
            <div v-else-if="image.isValid" class="image-preview">
              <img :src="image.url" class="mini-preview" />
            </div>
          </div>
          <button 
            class="add-image-btn" 
            v-if="uploadedImages.length < selectedAction.requiredImages"
            @click="addImageInput"
          >
            添加图片链接
          </button>
          <button 
            class="process-btn" 
            :class="{ 'active': isProcessBtnActive, 'processing': isProcessing }"
            :disabled="!isProcessBtnActive || isProcessing || credits < selectedAction.creditCost"
            @click="processImages"
          >
            {{ getBtnText() }}
          </button>
        </div>

        <!-- Photopea编辑器 -->
        <div v-if="showEditor" class="editor-module glass-effect">
          <div class="editor-header">
            <h3>图像处理中</h3>
            <div class="editor-close" @click="closeEditor">×</div>
          </div>
          <PhotopeaEditor
            :images="uploadedImages"
            :actionFile="selectedAction.actionFile"
            @process-complete="handleProcessComplete"
          />
        </div>

        <!-- 成图预览模块 -->
        <div class="result-preview-module glass-effect" v-if="resultImage">
          <div class="result-title">处理结果</div>
          <div class="result-image-container">
            <img :src="resultImage" class="result-img" />
          </div>
        </div>

        <!-- 成图调整模块 -->
        <div class="adjust-module glass-effect" v-if="resultImage">
          <div class="layer-select">
            <label>图层选择：</label>
            <select v-model="selectedLayer">
              <option v-for="layer in layers" :key="layer.id" :value="layer.id">
                {{ layer.name }}
              </option>
            </select>
          </div>
          <div class="opacity-adjust">
            <label>不透明度：{{ opacity }}%</label>
            <input 
              type="range" 
              min="0" 
              max="100" 
              v-model.number="opacity"
              class="slider"
            />
          </div>
          <div class="tools">
            <button class="eraser-btn" :class="{ 'active': eraserActive }" @click="toggleEraser">
              橡皮擦
            </button>
            <button class="save-btn" @click="saveImage">保存图片</button>
          </div>
        </div>
      </div>

      <!-- 我的页面 (示例内容) -->
      <div class="profile-page" v-if="activePage === 'profile'">
        <div class="profile-header glass-effect">
          <div class="avatar"></div>
          <div class="user-info">
            <div class="username">用户名123</div>
            <div class="user-credits">积分：{{ credits }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部导航栏 -->
    <div class="bottom-nav glass-effect">
      <div 
        class="nav-item" 
        :class="{ 'active': activePage === 'home' }"
        @click="activePage = 'home'"
      >
        <div class="nav-icon">🏠</div>
        <div class="nav-text">首页</div>
      </div>
      <div 
        class="nav-item" 
        :class="{ 'active': activePage === 'profile' }"
        @click="activePage = 'profile'"
      >
        <div class="nav-icon">👤</div>
        <div class="nav-text">我的</div>
      </div>
    </div>

    <!-- 充值模态框 -->
    <div class="modal-overlay" v-if="showRechargeModal" @click="showRechargeModal = false">
      <div class="modal-content glass-effect" @click.stop>
        <div class="modal-header">
          <h3>积分充值</h3>
          <div class="modal-close" @click="showRechargeModal = false">×</div>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <label>卡密：</label>
            <input type="text" v-model="rechargeCode" placeholder="请输入充值卡密" />
          </div>
          <button class="recharge-confirm-btn" @click="rechargeCredits">确认充值</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PhotopeaEditor from './components/PhotopeaEditor.vue';
import { getLocalActions, validateImageUrl } from './utils/axios';

export default {
  name: 'App',
  components: {
    PhotopeaEditor
  },
  data() {
    return {
      // 页面控制
      activePage: 'home',
      
      // 积分相关
      credits: 100,
      showRechargeModal: false,
      rechargeCode: '',
      
      // 动作预设相关
      showActionDropdown: false,
      actions: [],
      selectedAction: null,
      
      // 图像上传相关
      uploadedImages: [],
      isProcessing: false,
      
      // 编辑器相关
      showEditor: false,
      
      // 结果图像
      resultImage: null,
      
      // 图层调整相关
      layers: [
        { id: 1, name: '背景层' },
        { id: 2, name: '主体层' },
        { id: 3, name: '效果层' }
      ],
      selectedLayer: 1,
      opacity: 100,
      eraserActive: false
    }
  },
  computed: {
    isProcessBtnActive() {
      return this.uploadedImages.length === this.selectedAction.requiredImages 
        && this.credits >= this.selectedAction.creditCost
        && this.uploadedImages.every(img => img.isValid);
    }
  },
  created() {
    // 初始化动作列表
    this.initActions();
  },
  methods: {
    // 初始化动作列表
    initActions() {
      this.actions = getLocalActions();
      if (this.actions.length > 0) {
        this.selectedAction = this.actions[0];
      }
    },
    
    // 动作选择相关
    toggleActionDropdown() {
      this.showActionDropdown = !this.showActionDropdown;
    },
    selectAction(action) {
      this.selectedAction = action;
      this.uploadedImages = []; // 清空已上传图片
      this.showActionDropdown = false;
      this.resultImage = null; // 清空结果图片
    },
    
    // 图片上传相关
    addImageInput() {
      if (this.uploadedImages.length < this.selectedAction.requiredImages) {
        this.uploadedImages.push({
          url: '',
          isValid: false,
          loading: false,
          error: null
        });
      }
    },
    async validateImage(index) {
      const image = this.uploadedImages[index];
      
      // 如果URL为空，则不验证
      if (!image.url.trim()) {
        image.isValid = false;
        image.error = null;
        return;
      }
      
      // 开始验证
      image.loading = true;
      image.error = null;
      
      try {
        // 验证图片URL
        await validateImageUrl(image.url);
        image.isValid = true;
        image.error = null;
      } catch (error) {
        image.isValid = false;
        image.error = error.message;
      } finally {
        image.loading = false;
      }
    },
    removeImage(index) {
      this.uploadedImages.splice(index, 1);
    },
    
    // 获取按钮文本
    getBtnText() {
      if (this.isProcessing) return '处理中...';
      if (this.credits < this.selectedAction.creditCost) return `积分不足（需${this.selectedAction.creditCost}积分）`;
      return '开始处理';
    },
    
    // 图像处理相关
    processImages() {
      if (!this.isProcessBtnActive) return;
      
      // 检查积分是否足够
      if (this.credits < this.selectedAction.creditCost) {
        alert('积分不足，请充值');
        return;
      }
      
      this.isProcessing = true;
      this.showEditor = true;
    },
    
    // 处理完成回调
    handleProcessComplete(resultImageUrl) {
      // 扣除积分
      this.credits -= this.selectedAction.creditCost;
      
      // 设置结果图像
      this.resultImage = resultImageUrl;
      
      // 隐藏编辑器和加载状态
      this.showEditor = false;
      this.isProcessing = false;
    },
    
    // 关闭编辑器
    closeEditor() {
      this.showEditor = false;
      this.isProcessing = false;
    },
    
    // 图层调整相关
    toggleEraser() {
      this.eraserActive = !this.eraserActive;
    },
    saveImage() {
      // 创建下载链接
      const link = document.createElement('a');
      link.href = this.resultImage;
      link.download = `processed_image_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert('图片已保存！');
    },
    
    // 充值相关
    rechargeCredits() {
      if (this.rechargeCode.trim()) {
        // 模拟充值
        this.credits += 50;
        alert('充值成功！');
        this.showRechargeModal = false;
        this.rechargeCode = '';
      } else {
        alert('请输入有效的卡密！');
      }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: #e3e3e3;
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  max-width: 450px;
  margin: 0 auto;
  position: relative;
}

.content-area {
  flex: 1;
  padding: 15px;
  padding-bottom: 70px; /* 为底部导航栏留出空间 */
  overflow-y: auto;
}

/* 强化毛玻璃效果 */
.glass-effect {
  background: rgba(30, 39, 55, 0.65);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 模块通用样式 */
.credit-module,
.action-select-module,
.upload-module,
.result-preview-module,
.adjust-module,
.profile-header {
  padding: 16px;
  margin-bottom: 20px;
}

/* 积分模块 */
.credit-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.credit-amount {
  font-size: 16px;
}

.credit-number {
  font-weight: bold;
  color: #00b4d8;
}

/* 渐变按钮效果 */
.recharge-btn {
  padding: 8px 18px;
  background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.recharge-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.3);
}

/* 动作选择模块 */
.action-select {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-radius: 10px;
  background: rgba(40, 49, 65, 0.8);
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dropdown-icon {
  transition: transform 0.3s;
  font-size: 12px;
  color: #a0a0a0;
}

.dropdown-icon.active {
  transform: rotate(180deg);
}

.action-dropdown {
  margin-top: 8px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(40, 49, 65, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.action-item {
  padding: 12px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: background 0.2s ease;
}

.action-item:last-child {
  border-bottom: none;
}

.action-item:hover {
  background: rgba(67, 97, 238, 0.2);
}

/* 上传模块 */
.upload-title {
  margin-bottom: 15px;
  font-size: 16px;
  color: #f0f0f0;
}

.image-input-container {
  margin-bottom: 10px;
}

.image-input-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.image-url-input {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(40, 49, 65, 0.8);
  color: #f0f0f0;
  width: 100%;
  margin-right: 30px;
}

.image-loading, .image-error {
  margin-top: 5px;
  font-size: 12px;
  padding: 3px 0;
}

.image-loading {
  color: #aaa;
}

.image-error {
  color: #ff6b6b;
}

.image-preview {
  margin-top: 5px;
  max-width: 100%;
  max-height: 100px;
  overflow: hidden;
  border-radius: 8px;
}

.mini-preview {
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
}

.add-image-btn {
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
  border-radius: 8px;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  background: rgba(40, 49, 65, 0.6);
  color: #f0f0f0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-image-btn:hover {
  background: rgba(60, 69, 85, 0.8);
}

.preview-remove {
  position: absolute;
  right: 0;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.2s;
  border-radius: 4px;
}

.preview-remove:hover {
  background: rgba(255, 0, 0, 0.8);
}

.process-btn {
  width: 100%;
  padding: 12px;
  margin-top: 15px;
  border-radius: 10px;
  border: none;
  background: #333;
  color: #888;
  font-weight: bold;
  cursor: not-allowed;
  transition: all 0.3s ease;
}

.process-btn.active {
  background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
}

.process-btn.active:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(67, 97, 238, 0.4);
}

.process-btn.processing {
  background: #2a2a3a;
  color: #aaa;
  cursor: not-allowed;
}

/* 结果预览模块 */
.result-title {
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: bold;
  color: #f0f0f0;
}

.result-image-container {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

.result-img {
  width: 100%;
  display: block;
}

/* 调整模块 */
.layer-select,
.opacity-adjust {
  margin-bottom: 15px;
}

.layer-select select {
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(40, 49, 65, 0.8);
  color: #f0f0f0;
  width: 100%;
  margin-top: 5px;
  appearance: none;
}

.slider {
  width: 100%;
  margin-top: 8px;
  appearance: none;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.tools {
  display: flex;
  gap: 10px;
}

.eraser-btn,
.save-btn {
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
}

.eraser-btn {
  background: rgba(40, 49, 65, 0.8);
  color: #f0f0f0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.eraser-btn:hover {
  background: rgba(60, 69, 85, 0.8);
}

.eraser-btn.active {
  background: linear-gradient(135deg, #3f3f3f 0%, #1f1f1f 100%);
  color: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

.save-btn {
  background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
  color: white;
  box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(67, 97, 238, 0.4);
}

/* 底部导航栏 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 450px;
  margin: 0 auto;
  display: flex;
  height: 65px;
  border-radius: 20px 20px 0 0;
  overflow: hidden;
  z-index: 100;
  backdrop-filter: blur(20px);
  background: rgba(30, 39, 55, 0.8);
  box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.3);
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #888;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-item:hover {
  color: #aaa;
}

.nav-item.active {
  color: #4361ee;
}

.nav-icon {
  font-size: 20px;
  margin-bottom: 4px;
}

.nav-text {
  font-size: 12px;
}

/* 模态框 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.modal-content {
  width: 85%;
  max-width: 350px;
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h3 {
  color: #f0f0f0;
}

.modal-close {
  font-size: 20px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #f0f0f0;
  transition: background 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

.modal-body {
  padding: 20px;
}

.input-group {
  margin-bottom: 20px;
}

.input-group label {
  display: block;
  margin-bottom: 8px;
  color: #f0f0f0;
}

.input-group input {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(40, 49, 65, 0.8);
  color: #f0f0f0;
}

.recharge-confirm-btn {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
}

.recharge-confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(67, 97, 238, 0.4);
}

/* 个人页面 */
.profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
}

.avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3f3f3f 0%, #1f1f1f 100%);
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.username {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #f0f0f0;
}

.user-credits {
  color: #aaa;
}

/* 适配深色模式滚动条 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(30, 39, 55, 0.5);
}

::-webkit-scrollbar-thumb {
  background: rgba(67, 97, 238, 0.6);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(67, 97, 238, 0.8);
}

/* 添加编辑器模块样式 */
.editor-module {
  margin-bottom: 20px;
  overflow: hidden;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.editor-header h3 {
  color: #f0f0f0;
  margin: 0;
}

.editor-close {
  font-size: 20px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #f0f0f0;
  transition: background 0.2s;
}

.editor-close:hover {
  background: rgba(255, 255, 255, 0.1);
}
</style>
